import { createContext, useState, useCallback, type ReactNode } from 'react';

export interface GlobalNotification {
    id: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
}

export interface ErrorDialogProps {
    title: string;
    message: string;
    details?: string;
}

export interface ConfirmationDialogProps {
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
}

export interface AlertDialogProps {
    title: string;
    message: string;
}

export interface IAppContext {
    isLoading: boolean;
    setIsLoading: (v: boolean) => void;
    isNavbarLoaded: boolean;
    setIsNavbarLoaded: (v: boolean) => void;
    menuExtended: boolean;
    setMenuExtended: (v: boolean) => void;
    navigationVisible: boolean;
    setNavigationVisible: (v: boolean) => void;
    globalNotifications: GlobalNotification[];
    addGlobalNotification: (n: Omit<GlobalNotification, 'id'>) => void;
    clearGlobalNotification: (id: string) => void;
    errorDialogProps: ErrorDialogProps | null;
    setErrorDialogProps: (p: ErrorDialogProps | null) => void;
    confirmationDialogProps: ConfirmationDialogProps | null;
    setConfirmationDialogProps: (p: ConfirmationDialogProps | null) => void;
    alertDialogProps: AlertDialogProps | null;
    setAlertDialogProps: (p: AlertDialogProps | null) => void;
}

export const AppContext = createContext<IAppContext>({} as IAppContext);

export function AppProvider({ children }: { children: ReactNode }) {
    const [isLoading, setIsLoading] = useState(false);
    const [isNavbarLoaded, setIsNavbarLoaded] = useState(false);
    const [menuExtended, setMenuExtended] = useState(
        () => localStorage.getItem('verticalNavigationExpanded') !== 'false'
    );
    const [navigationVisible, setNavigationVisible] = useState(true);
    const [globalNotifications, setGlobalNotifications] = useState<GlobalNotification[]>([]);
    const [errorDialogProps, setErrorDialogProps] = useState<ErrorDialogProps | null>(null);
    const [confirmationDialogProps, setConfirmationDialogProps] = useState<ConfirmationDialogProps | null>(null);
    const [alertDialogProps, setAlertDialogProps] = useState<AlertDialogProps | null>(null);

    const addGlobalNotification = useCallback((n: Omit<GlobalNotification, 'id'>) => {
        const id = crypto.randomUUID();
        setGlobalNotifications(prev => [...prev, { ...n, id }]);
        setTimeout(() => {
            setGlobalNotifications(prev => prev.filter(x => x.id !== id));
        }, 5000);
    }, []);

    const clearGlobalNotification = useCallback((id: string) => {
        setGlobalNotifications(prev => prev.filter(x => x.id !== id));
    }, []);

    const ctx: IAppContext = {
        isLoading, setIsLoading,
        isNavbarLoaded, setIsNavbarLoaded,
        menuExtended, setMenuExtended: (v: boolean) => {
            localStorage.setItem('verticalNavigationExpanded', v.toString());
            setMenuExtended(v);
        },
        navigationVisible, setNavigationVisible,
        globalNotifications, addGlobalNotification, clearGlobalNotification,
        errorDialogProps, setErrorDialogProps,
        confirmationDialogProps, setConfirmationDialogProps,
        alertDialogProps, setAlertDialogProps,
    };

    return <AppContext.Provider value={ctx}>{children}</AppContext.Provider>;
}
